import React, { useEffect, useRef, useState } from "react";

function useCountUp(target, durationMs = 1800) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || started.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const step = (now) => {
              const t = Math.min(1, (now - start) / durationMs);
              const eased = 1 - Math.pow(1 - t, 3);
              setValue(Math.round(target * eased));
              if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
          }
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [target, durationMs]);
  return [value, ref];
}

export default function Hero() {
  const [count, badgeRef] = useCountUp(20);
  return (
    <section
      id="top"
      className="relative flex items-center overflow-hidden"
      style={{ minHeight: "100vh" }}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 hero-grid opacity-60" aria-hidden />
      <div className="absolute inset-0 hero-glow" aria-hidden />

      <div className="container-nk relative z-10 pt-32 pb-24">
        <div className="max-w-[1100px]">
          <p
            className="eyebrow fade-up"
            style={{ animationDelay: "0.05s" }}
            data-testid="hero-eyebrow"
          >
            Scotland's Largest Purpose-Built Delivery Kitchens
          </p>

          <h1
            className="font-display fade-up mt-5 leading-[0.92]"
            style={{
              fontSize: "clamp(68px, 12vw, 148px)",
              animationDelay: "0.15s",
            }}
            data-testid="hero-headline"
          >
            Twenty Brands.
            <br />
            <span className="text-gold">One Roof.</span>
          </h1>

          <p
            className="fade-up mt-4 font-mono text-[12px] md:text-[13px] uppercase tracking-[0.18em] text-gold/80"
            style={{ animationDelay: "0.22s" }}
            data-testid="hero-tagline-extra"
          >
            <span className="text-gold">●</span>  Plus more brands joining soon
          </p>

          <p
            className="fade-up mt-7 max-w-[640px] text-[17px] leading-relaxed text-[var(--text)]/80"
            style={{ animationDelay: "0.28s" }}
            data-testid="hero-subtext"
          >
            Walk in and order at the counter, or get it delivered to your door
            across Edinburgh — every craving, every cuisine, from twenty
            state-of-the-art kitchens.
          </p>

          <div
            className="fade-up flex flex-wrap gap-4 mt-10"
            style={{ animationDelay: "0.42s" }}
          >
            <a
              href="#brands"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("brands")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="btn-primary"
              data-testid="hero-cta-explore"
            >
              Explore Our Brands
              <span aria-hidden>→</span>
            </a>
            <a
              href="#how"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="btn-ghost"
              data-testid="hero-cta-how"
            >
              How It Works
            </a>
          </div>
        </div>

        {/* Corner badge — desktop only */}
        <div
          ref={badgeRef}
          className="hidden lg:flex absolute top-32 right-12 flex-col items-center justify-center w-[150px] h-[150px] rounded-full border border-[var(--border)] bg-[var(--surface)]/40 backdrop-blur-sm fade-up"
          style={{ animationDelay: "0.55s" }}
          data-testid="hero-corner-badge"
        >
          <span className="font-display text-[60px] text-gold leading-none tabular-nums" data-testid="hero-corner-count">
            {count}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-2 text-center">
            Brands<br />Under One Roof
          </span>
        </div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted flex items-center gap-3">
        <span className="h-px w-8 bg-[var(--muted)]" />
        Scroll
        <span className="h-px w-8 bg-[var(--muted)]" />
      </div>
    </section>
  );
}
